import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VerifyEmailPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        const response = await axios.get(`${API}/auth/verify-email/${token}`);
        setStatus('success');
        setMessage(response.data.message);
        setEmail(response.data.email);
      } catch (error) {
        setStatus('error');
        setMessage(error.response?.data?.detail || 'Verification failed. The link may be invalid or expired.');
      }
    };

    if (token) {
      verifyEmail();
    }
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md text-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          {status === 'verifying' && (
            <>
              <Loader2 className="w-16 h-16 text-blue-500 animate-spin mx-auto mb-6" />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Verifying Your Email</h1>
              <p className="text-gray-600">Please wait while we verify your email address...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Email Verified!</h1>
              <p className="text-gray-600 mb-6">
                {email && (
                  <>
                    Your email <span className="font-medium text-gray-900">{email}</span> has been verified successfully.
                  </>
                )}
              </p>
              <p className="text-gray-600 mb-6">
                You can now log in to your DJ CRM account and start your free trial.
              </p>
              <Button 
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:opacity-90"
                onClick={() => navigate('/auth')}
              >
                Go to Login
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Verification Failed</h1>
              <p className="text-gray-600 mb-6">{message}</p>
              <div className="space-y-3">
                <Button 
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:opacity-90"
                  onClick={() => navigate('/auth')}
                >
                  Go to Login
                </Button>
                <p className="text-sm text-gray-500">
                  Need a new verification link? Log in with your credentials and we'll send you a new one.
                </p>
              </div>
            </>
          )}
        </div>
        
        <p className="text-center mt-6 text-sm text-gray-500">
          <Link to="/" className="text-blue-600 hover:text-blue-700">
            &larr; Back to home
          </Link>
        </p>
      </div>
    </div>
  );
}
